import type { PublisherRelease } from '../types/domain';

const owns = (value: Record<string, string>, key: string) =>
  Object.prototype.hasOwnProperty.call(value, key);

/** Reads one track's sheet while preserving older releases that stored one shared value. */
export function publisherLyricsForTrack(release: PublisherRelease, songId: string): string {
  const saved = release.trackLyrics ?? {};
  if (owns(saved, songId)) return saved[songId] ?? '';
  return release.songIds[0] === songId ? (release.lyrics ?? '') : '';
}

/**
 * Produces a release-safe map containing selected tracks only. Song lyrics may be supplied as
 * defaults when a draft has never stored an explicit value for that track.
 */
export function normalizePublisherTrackLyrics(
  release: PublisherRelease,
  defaults: Readonly<Record<string, string>> = {}
): Record<string, string> {
  return Object.fromEntries(
    release.songIds.map((songId) => {
      const saved = release.trackLyrics ?? {};
      if (owns(saved, songId)) return [songId, saved[songId] ?? ''];
      if (release.songIds[0] === songId && release.lyrics) return [songId, release.lyrics];
      return [songId, defaults[songId] ?? ''];
    })
  );
}

/** Keeps the legacy first-track field in sync while clients with older saved data still exist. */
export function publisherLyricsPatch(
  songIds: readonly string[],
  trackLyrics: Record<string, string>
): Pick<PublisherRelease, 'trackLyrics' | 'lyrics'> {
  return {
    trackLyrics,
    lyrics: songIds[0] ? (trackLyrics[songIds[0]] ?? '') : '',
  };
}
